/** Radiance RGBE / XYZE (.hdr) decoder with new- and old-style run-length encoding. */
import { mat3Inv, mat3Vec, PRIMARIES, rgbToXyzMatrix } from '../../core/color';
import type { RawFrame } from '../../core/types';
import { makeFrame } from '../frame';

function readLine(b: Uint8Array, o: number): [string, number] {
  let s = '';
  while (o < b.length && b[o] !== 0x0a) s += String.fromCharCode(b[o++]);
  return [s, o + 1];
}

export function decodeHdr(bytes: Uint8Array, name: string): RawFrame {
  let o = 0;
  let line: string;
  let format = '32-bit_rle_rgbe';
  let exposure = 1;
  const info: Record<string, string> = {};
  [line, o] = readLine(bytes, o);
  if (!line.startsWith('#?')) throw new Error('Not a Radiance HDR file');
  for (;;) {
    [line, o] = readLine(bytes, o);
    if (o >= bytes.length) throw new Error('Radiance header not terminated');
    if (line === '') break;
    const [k, v] = line.split('=');
    if (k === 'FORMAT') format = v.trim();
    else if (k === 'EXPOSURE') exposure *= parseFloat(v);
    else if (k && v !== undefined) info[k.trim()] = v.trim();
  }
  [line, o] = readLine(bytes, o);
  const m = line.trim().match(/^([+-])([YX])\s+(\d+)\s+([+-])([YX])\s+(\d+)$/);
  if (!m) throw new Error(`Unsupported Radiance resolution string: ${line}`);
  const majorLen = parseInt(m[3], 10);
  const minorLen = parseInt(m[6], 10);
  const majorIsY = m[2] === 'Y';
  const width = majorIsY ? minorLen : majorLen;
  const height = majorIsY ? majorLen : minorLen;
  const scan = new Uint8Array(minorLen * 4);
  const out = new Float32Array(width * height * 3);
  const put = (major: number, minor: number) => {
    let x: number;
    let y: number;
    if (majorIsY) {
      y = m[1] === '-' ? major : height - 1 - major;
      x = m[4] === '+' ? minor : width - 1 - minor;
    } else {
      x = m[1] === '+' ? major : width - 1 - major;
      y = m[4] === '-' ? minor : height - 1 - minor;
    }
    return (y * width + x) * 3;
  };
  for (let j = 0; j < majorLen; j++) {
    if (minorLen >= 8 && minorLen < 32768 && bytes[o] === 2 && bytes[o + 1] === 2 && !(bytes[o + 2] & 0x80)) {
      if (((bytes[o + 2] << 8) | bytes[o + 3]) !== minorLen) throw new Error('Radiance scanline length mismatch');
      o += 4;
      for (let c = 0; c < 4; c++) {
        let i = 0;
        while (i < minorLen) {
          let count = bytes[o++];
          if (count > 128) {
            count -= 128;
            const v = bytes[o++];
            for (let k = 0; k < count; k++) scan[i++ * 4 + c] = v;
          } else for (let k = 0; k < count; k++) scan[i++ * 4 + c] = bytes[o++];
        }
      }
    } else {
      // Flat or old-style RLE (1,1,1,n repeats the previous pixel).
      let i = 0;
      let shift = 0;
      while (i < minorLen) {
        const r = bytes[o];
        const g = bytes[o + 1];
        const b = bytes[o + 2];
        const e = bytes[o + 3];
        o += 4;
        if (r === 1 && g === 1 && b === 1 && i > 0) {
          const count = e << shift;
          for (let k = 0; k < count; k++, i++) scan.copyWithin(i * 4, (i - 1) * 4, i * 4);
          shift += 8;
        } else {
          scan[i * 4] = r;
          scan[i * 4 + 1] = g;
          scan[i * 4 + 2] = b;
          scan[i * 4 + 3] = e;
          i++;
          shift = 0;
        }
      }
    }
    for (let i = 0; i < minorLen; i++) {
      const e = scan[i * 4 + 3];
      const p = put(j, i);
      if (e === 0) {
        out[p] = out[p + 1] = out[p + 2] = 0;
        continue;
      }
      const f = Math.pow(2, e - 136) / exposure;
      out[p] = (scan[i * 4] + 0.5) * f;
      out[p + 1] = (scan[i * 4 + 1] + 0.5) * f;
      out[p + 2] = (scan[i * 4 + 2] + 0.5) * f;
    }
  }
  const xyz = format.includes('xyze');
  if (xyz) {
    const M = mat3Inv(rgbToXyzMatrix(PRIMARIES.srgb.c));
    const v: [number, number, number] = [0, 0, 0];
    for (let i = 0; i < out.length; i += 3) {
      v[0] = out[i];
      v[1] = out[i + 1];
      v[2] = out[i + 2];
      const r = mat3Vec(M, v);
      out[i] = r[0];
      out[i + 1] = r[1];
      out[i + 2] = r[2];
    }
  }
  if (exposure !== 1) info.EXPOSURE = `${exposure} (divided out)`;
  return makeFrame({
    width,
    height,
    data: out,
    channels: 3,
    isFloat: true,
    bitDepth: 32,
    format: 'hdr',
    name,
    encoding: {
      transfer: 'linear',
      primaries: 'srgb',
      source: 'format',
      description: xyz ? 'Radiance XYZE, converted to linear sRGB primaries' : 'Radiance RGBE: linear radiance, sRGB primaries assumed',
    },
    info: { Format: format, ...info },
  });
}
